import {EventEmitter, Injectable} from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { Observable } from 'rxjs';
import {ServerService} from "./ServerService";
import {HttpClient} from "@angular/common/http";

@Injectable({
  providedIn: 'root'
})
export class SocketService extends ServerService {
  private socket: Socket;
  private tabId: string;
  private isMasterTab = false;
  private broadcastChannel: BroadcastChannel;
  private renovacaoIntervalId: any;
  conectado = false;
  desconectou = false;
  onReconectou = new EventEmitter();
  constructor(protected http: HttpClient) {
    super(http);
    // Gerar ID único para esta aba
    this.tabId = this.gerarTabId();

    // Configurar BroadcastChannel para comunicação entre abas (versão nova para evitar colisões)
    this.broadcastChannel = new BroadcastChannel('socket_tab_control_v2');
    this.configurarControleAbas();
    this.iniciarVigilanciaExpiracaoLock();

    const host = window.location.hostname;
    let socketUrl = `https://${host}`;
    const isLocal = host === 'localhost' || host === '127.0.0.1';

    let ops: any =  {
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 2000,
      timeout: 5000,
      pingInterval: 5000,
      pingTimeout: 3000,
      query: {
        tabId: this.tabId,
        timestamp: Date.now()
      }
    }

    if(isLocal){
      socketUrl =  (window.location.protocol === 'https:' ? 'wss://localhost:3000' : 'ws://localhost:3000')
      ops.rejectUnauthorized = false;
    } else {
      ops.path =  '/testesocketio'
    }

    console.log(socketUrl)
    console.log(ops)

    this.socket = io(socketUrl, ops);

    this.on('reload').subscribe(() => {
      window.location.reload();
      window.parent.postMessage({tipo: "NOVA_MENSAGEM", text: {tipo: 'RELOAD'}}, "*");
    });

    this.on('connect').subscribe(() => {
      console.log('[SOCKET] connect - Tab:', this.tabId);
      this.conectado = true;
      this.verificarControleTab();
      if(this.desconectou)
        this.onReconectou.emit()
    });

    this.on('connect_timeout').subscribe(() => {
      console.warn('[SOCKET] timeout ao conectar');
      this.conectado = false;
    });

    this.on('disconnect').subscribe((reason) => {
      console.warn('[SOCKET] disconnect:', reason);
      this.conectado = false;
      this.desconectou = true;
    });

    this.on('connect_error').subscribe((err) => {
      console.error('[SOCKET] connect_error:');
      console.error(err)
      if(this.conectado)
        this.desconectou = true;

      this.conectado = false;
    });

    this.on('reconnect_attempt').subscribe(() => {
      console.log('[SOCKET] reconnect_attempt');
    });

    this.on('reconnect_failed').subscribe(() => {
      console.error('[SOCKET] falhou ao tentar reconectar');
    });

    // Detectar fechamento da aba
    window.addEventListener('beforeunload', () => {
      // Não liberar o controle ao fechar a master.
      // Mantemos o lock até expirar para impedir reeleição imediata.
    });

    // Sincronizar mudanças de lock entre abas em tempo real
    window.addEventListener('storage', (e: StorageEvent) => {
      if (e.key === 'socket_master_tab') {
        try {
          const lock = e.newValue ? JSON.parse(e.newValue) : null;
          if (lock && lock.tabId === this.tabId) {
            // Esta aba reconhece-se como master
            this.isMasterTab = true;
            this.iniciarRenovacaoLock();
          } else {
            this.isMasterTab = false;
          }

          // Se outra aba tentar assumir e esta aba ainda é a master, reassegure o lock imediatamente
          if (this.isMasterTab && lock && lock.tabId !== this.tabId) {
            console.log('[TAB CONTROL] Tentativa de preempção detectada. Reafirmando lock.');
            this.renovarLockTab();
          }
        } catch (_) {
          // ignore JSON errors
        }
      }
    });
  }

  private gerarTabId(): string {
    // Persistir o tabId no sessionStorage para sobreviver a reloads (F5)
    const existente = sessionStorage.getItem('socket_tab_id');
    if (existente) {
      return existente;
    }
    const novo = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('socket_tab_id', novo);
    return novo;
  }

  private configurarControleAbas(): void {
    this.broadcastChannel.onmessage = (event) => {
      const { type, tabId, data } = event.data;
      console.log('[TAB CONTROL] chegou novo mensagem tipo ' + type);
      switch (type) {
        case 'TAB_RELEASING_CONTROL':
          // Não reeleger automaticamente quando a master libera.
          // A próxima eleição só ocorre por expiração do lock.
          if (tabId !== this.tabId) {
            console.log('[TAB CONTROL] Outra aba liberou controle (sem reeleição automática)');
          }
          break;

        case 'FORCE_DISCONNECT':
          if (tabId === this.tabId) {
            console.log('[TAB CONTROL] Forçado a desconectar (ignorado para master)');
            // Ignorado para evitar queda indevida; apenas abas não-master podem agir.
          }
          break;
      }
    };
  }

  private verificarControleTab(): void {
    const lockData = localStorage.getItem('socket_master_tab');
    const agora = Date.now();
    const LOCK_TIMEOUT = 15000; // 15 segundos
    const GRACE_MS = 3000; // janela de graça pós-expiração para o dono anterior

    if (!lockData) {
      console.log('[TAB CONTROL] Sem lock - assumindo');
      this.assumirControleTab();
      return;
    }

    let lock: any;
    try {
      lock = JSON.parse(lockData);
    } catch (_) {
      console.warn('[TAB CONTROL] Lock inválido - reassumindo');
      this.assumirControleTab();
      return;
    }

    const expirou = agora > lock.expires;
    const emGraca = agora <= (lock.graceUntil || 0);

    if (!expirou) {
      if (lock.tabId === this.tabId) {
        console.log('[TAB CONTROL] Sou master - renovando');
        this.isMasterTab = true;
        this.renovarLockTab();
      } else {
        console.log('[TAB CONTROL] Outra aba é master - visualização');
      }
      return;
    }

    // Expirou
    if (emGraca && lock.tabId !== this.tabId) {
      console.log('[TAB CONTROL] Lock expirado mas em janela de graça do dono anterior - aguardando');
      return;
    }

    // Sem graça ou sou o dono anterior: posso assumir
    console.log('[TAB CONTROL] Assumindo após expiração');
    this.assumirControleTab();
  }

  private assumirControleTab(): void {
    const lockData = {
      tabId: this.tabId,
      timestamp: Date.now(),
      expires: Date.now() + 15000, // 15 segundos
      graceUntil: Date.now() + 15000 + 3000 // 3s de graça
    };

    localStorage.setItem('socket_master_tab', JSON.stringify(lockData));
    // Verificação pós-gravação para evitar condição de corrida
    try {
      const gravado = JSON.parse(localStorage.getItem('socket_master_tab'));
      if (gravado && gravado.tabId === this.tabId) {
        this.isMasterTab = true;
        this.iniciarRenovacaoLock();
        console.log('[TAB CONTROL] Esta aba assumiu controle do socket - Tab ID:', this.tabId);
      } else {
        this.isMasterTab = false;
        console.log('[TAB CONTROL] Perdeu corrida para master. Permanecerá em visualização.');
      }
    } catch (_) {
      // Em caso de erro, não assumir
      this.isMasterTab = false;
    }
  }

  private liberarControleTab(): void {
    // Mantém o lock para evitar reeleição imediata; apenas deixa de renovar.
    this.isMasterTab = false;
    console.log('[TAB CONTROL] Master encerrando sem liberar lock (aguarda expiração)');
    // Opcionalmente, poderíamos sinalizar, mas sem acionar reeleição:
    // this.broadcastChannel.postMessage({ type: 'TAB_RELEASING_CONTROL', tabId: this.tabId });
  }

  private renovarLockTab(): void {
    // Escrever lock de forma idempotente: só renovar se ainda somos o dono
    try {
      const atualStr = localStorage.getItem('socket_master_tab');
      const atual = atualStr ? JSON.parse(atualStr) : null;
      if (!atual || atual.tabId !== this.tabId) {
        // Outro tab assumiu; não sobrescrever
        this.isMasterTab = false;
        return;
      }
    } catch (_) {
      // Se corrompido, não renova
      this.isMasterTab = false;
      return;
    }

    const lockData = {
      tabId: this.tabId,
      timestamp: Date.now(),
      expires: Date.now() + 15000,
      graceUntil: Date.now() + 15000 + 3000
    };
    localStorage.setItem('socket_master_tab', JSON.stringify(lockData));
  }

  private iniciarRenovacaoLock(): void {
    if (this.renovacaoIntervalId) return;
    this.renovacaoIntervalId = setInterval(() => {
      if (this.isMasterTab) {
        this.renovarLockTab();
      }
    }, 4000); // Renovar a cada ~4s para não expirar
  }

  private iniciarVigilanciaExpiracaoLock(): void {
    // Abas não-mestras verificam periodicamente se o lock expirou
    setInterval(() => {
      if (!this.isMasterTab) {
        this.verificarControleTab();
      }
    }, 3000);
  }

  private desconectarSocket(): void {
    // Só desconectar se não for master tab
    if (!this.isMasterTab && this.socket && this.socket.connected) {
      console.log('[TAB CONTROL] Desconectando socket - não é master tab');
      this.socket.disconnect();
      this.conectado = false;
    } else if (this.isMasterTab) {
      console.log('[TAB CONTROL] Não desconectando - esta é a master tab');
    }
  }

  emit(eventName: string, data: any): void {
    this.socket.emit(eventName, data);
  }

  atualizeAbas() {
    return this.obtenha('/sockets/atualizeAbas', {});
  }

  // Método para ouvir eventos do servidor
  on(eventName: string): Observable<any> {
    return new Observable<any>(observer => {
      this.socket.on(eventName, (data: any) => {
        observer.next(data);
      });

      // Limpeza quando o observable é destruído
      return () => this.socket.off(eventName);
    });
  }

  // Método público para verificar se esta aba é a master
  isMaster(): boolean {
    return this.isMasterTab;
  }

  // Método público para forçar reconexão (útil para debug)
  forcarReconexao(): void {
    if (this.isMasterTab) {
      this.socket.disconnect();
      setTimeout(() => {
        this.socket.connect();
      }, 1000);
    }
  }

  // Método público para limpar controle de abas (útil para debug)
  limparControleAbas(): void {
    localStorage.removeItem('socket_master_tab');
    console.log('[TAB CONTROL] Controle de abas limpo - próxima aba será master');
  }

  // Método público para debug do status da aba
  debugStatus(): void {
    const lockData = localStorage.getItem('socket_master_tab');
    console.log('[TAB CONTROL] DEBUG STATUS:');
    console.log('- Tab ID:', this.tabId);
    console.log('- É Master:', this.isMasterTab);
    console.log('- Socket Conectado:', this.conectado);
    console.log('- Lock Data:', lockData ? JSON.parse(lockData) : 'Nenhum lock');
  }


}
